import { FC, ReactNode, useEffect, useState } from "react";
import Header from "./main/Header";
import NavBar from "./main/NavBar";
import Footer from "./main/Footer";
import { defaultPalette } from "../theme/defaultTheme";
import { Box, Stack, ThemeProvider, createTheme } from "@mui/material";
import SideBar from "./main/SideBar";
import ImageTitles from "./main/ImageTitles";
import ScrollToTop from "./test/ScrollToTop";

export interface LayoutProps {
  children: ReactNode;
}

const Layout: FC<LayoutProps> = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);

  // Function to toggle dark/light mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Function to generate Material-UI theme based on mode
  const generateTheme = (darkMode: boolean) => {
    const mode = darkMode ? "dark" : "light";
    const palette = defaultPalette.default[mode]; // Get colors for selected mode from defaultPalette
    return createTheme({ palette });
  };

  const [parentPath, setParentPath] = useState<string>("/");
  const [currentPath, setCurrentPath] = useState<string | null>("");
  const pathArray: Array<string> = window.location.pathname.split("/");
  const pathLength: number = pathArray.length;
  useEffect(() => {
    if (pathLength === 3) {
      setCurrentPath(pathArray[2]);
      setParentPath(parentPath.concat(pathArray[1]));
    } else {
      setParentPath(parentPath.concat(pathArray[1]));
      setCurrentPath(null);
    }
  }, []);

  return (
    <ThemeProvider theme={generateTheme(darkMode)}>
      <Box
        sx={{
          backgroundColor: "background.paper",
          color: "text.primary",
          position: "relative",
          minHeight: "100vh",
          overflowX: "hidden",
        }}
      >
        <Stack direction="column" spacing={2} sx={{ mt: { xs: 1 } }}>
          <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
          <div id="back-to-top-anchor"></div>
          <NavBar
            parentPath={parentPath}
            currentPath={currentPath}
            setParentPath={setParentPath}
            setCurrentPath={setCurrentPath}
          />
          <ImageTitles />
          {pathArray[1].length === 0 || parentPath === null ? (
            <>{children}</>
          ) : (
            <Stack
              direction={{ xs: "column-reverse", md: "row" }}
              spacing={2}
              alignItems={"flex-start"}
              justifyContent={"center"}
            >
              <Box sx={{ width: { md: "70%" }, pb: 4, p: 2 }}>{children}</Box>
              <SideBar
                parentPath={parentPath}
                currentPath={currentPath}
                setParentPath={setParentPath}
                setCurrentPath={setCurrentPath}
              />
            </Stack>
          )}
          <ScrollToTop>
            <Box
              sx={{
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                backgroundColor: "#514e1b",
                color: "white",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                cursor: "pointer",
              }}
            >
              ↑
            </Box>
          </ScrollToTop>
          <Footer />
        </Stack>
      </Box>
    </ThemeProvider>
  );
};

export default Layout;
